// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * First-session consent banner.
 *
 * AYNI: telemetry is off until the viewer allows it (see
 * `analytics/config.ts`), and this strip is where they are asked.
 * Five pieces:
 *   - one line saying what would be reported and that nothing is
 *     sent without a yes
 *   - "Allow" and "No thanks", styled alike so refusing is as easy
 *     as accepting
 *   - a "Privacy settings" button that opens privacyUI
 *   - a "Read policy" link to /privacy
 *
 * The answer is the stored telemetry tier itself (`essential` or
 * `off`), so there is no separate "seen" flag: the banner shows
 * while no tier is stored and never again after. Ignoring it leaves
 * telemetry off. On "Allow" we emit a `settings_changed` event with
 * key `disclosure_seen`; a refusal sends nothing.
 *
 * AYNI (2026-10-07): a second, separate question below the first:
 * may the app remember, on this device only, which datasets the viewer
 * opens (Browse's Recent tab, "new since your last visit")? That memory
 * is opt-in (`services/visitMemory.ts`), so it gets its own "Remember"
 * and "No thanks", styled alike. Each question is answered on its own and
 * leaves the banner when answered; the banner goes once both are. Someone
 * who answered the telemetry question before the second one existed sees
 * only the second. "Privacy settings" answers no to whatever is still
 * open and opens the panel where both can be changed.
 *
 * The banner does not block the app — it's a non-modal strip at the
 * top of the viewport with `aria-live="polite"` so screen readers
 * announce it without stealing focus.
 *
 * On small viewports (≤ 600 px wide OR ≤ 480 px tall — typical for
 * embedded iframe demos and phones) the full banner would dominate
 * the available real estate and obscure the globe. In that case we
 * render a small pulsing shield badge in the top-left corner instead;
 * tapping the badge expands to the full banner content. The choice
 * is made once at show time — no resize listener — to keep the
 * behaviour predictable.
 */

import { applyTierChange, emit, hasStoredTier, setTier } from '../analytics'
import type { TelemetryTier } from '../types'
import { openPrivacyUI } from './privacyUI'
import { setVisitMemoryOn, visitMemoryChoice, VISITS_OFF_STORAGE_KEY, VISITS_ON_STORAGE_KEY } from '../services/visitMemory'
import { t, tHtml } from '../i18n'
import { announcePolite } from './domUtils'

/** The flag the pre-consent banner stored on "Got it". Reading that
 *  notice was not a choice, so the flag is not honoured; it is only
 *  cleared when the viewer answers. */
const LEGACY_SEEN_KEY = 'sos-disclosure-seen'

/** The telemetry tier's own storage key, for the test reset. */
const TIER_STORAGE_KEY = 'sos-telemetry-config'

/** Below either of these dimensions, render the badge instead of
 *  the full banner. Picked to match the existing privacy-ui.css
 *  ≤ 600 px breakpoint plus a height check that catches embedded
 *  demos with short aspect ratios. */
const SMALL_VIEWPORT_WIDTH = 600
const SMALL_VIEWPORT_HEIGHT = 480

/** How long the badge pulses before settling into a quiet rest
 *  state. Pulse is for attention on first appearance, not nagging. */
const PULSE_DURATION_MS = 6000

let mounted = false
/** Tracks the badge's pulse-settle setTimeout so dispose / dismiss
 *  / expand paths can cancel it. Without this, a stale callback
 *  from a previous show could later fire after a new badge has
 *  been mounted and silently strip its pulse. */
let pulseTimer: ReturnType<typeof setTimeout> | null = null

/** Cancel any pending pulse-settle timer. Idempotent. */
function clearPulseTimer(): void {
  if (pulseTimer != null) {
    clearTimeout(pulseTimer)
    pulseTimer = null
  }
}

/** Can this browser keep an answer at all? Locked-down storage (private
 *  mode, some school devices) counts as answered, so the banner doesn't
 *  nag on every reload when the answer can't be kept. Telemetry and visit
 *  memory both stay off there. */
function storageUsable(): boolean {
  try {
    localStorage.getItem(TIER_STORAGE_KEY)
    return true
  } catch {
    return false
  }
}

/** AYNI: is the telemetry question still open? */
function telemetryOpen(): boolean {
  return storageUsable() && !hasStoredTier()
}

/** AYNI: is the visit memory question still open? */
function visitsOpen(): boolean {
  return storageUsable() && visitMemoryChoice() === null
}

/** Has the viewer already answered both questions (or chosen in
 *  Tools → Privacy)? */
export function hasSeenDisclosure(): boolean {
  return !telemetryOpen() && !visitsOpen()
}

/** Drop the old banner's flag. Silent on storage failure. */
function clearLegacySeen(): void {
  try {
    localStorage.removeItem(LEGACY_SEEN_KEY)
  } catch {
    // ignore
  }
}

/** True when the viewport is small enough that the full banner
 *  would obscure most of the app. Exported for tests. */
export function isSmallViewport(): boolean {
  return (
    window.innerWidth <= SMALL_VIEWPORT_WIDTH ||
    window.innerHeight <= SMALL_VIEWPORT_HEIGHT
  )
}

/** Build + attach the full banner DOM: the questions still open, then
 *  the settings shortcut and the policy link. */
function buildBanner(): HTMLElement {
  const banner = document.createElement('section')
  banner.id = 'disclosure-banner'
  banner.className = 'disclosure-banner'
  banner.setAttribute('role', 'region')
  // setAttribute is API-level (browser handles attribute escaping
  // internally), so plain t() is safe here. innerHTML below is
  // string-level: every translated value flows through tHtml/tAttr.
  banner.setAttribute('aria-label', t('disclosureBanner.aria'))
  banner.setAttribute('aria-live', 'polite')
  const telemetry = telemetryOpen()
    ? `
    <div class="disclosure-banner-question" id="disclosure-banner-telemetry" role="group" aria-labelledby="disclosure-banner-text">
      <p class="disclosure-banner-text" id="disclosure-banner-text">${tHtml('disclosureBanner.text')}</p>
      <div class="disclosure-banner-actions">
        <button
          type="button"
          id="disclosure-banner-allow"
          class="disclosure-banner-btn disclosure-banner-btn-primary"
        >${tHtml('disclosureBanner.allow')}</button>
        <button
          type="button"
          id="disclosure-banner-decline"
          class="disclosure-banner-btn disclosure-banner-btn-primary"
        >${tHtml('disclosureBanner.decline')}</button>
      </div>
    </div>`
    : ''
  const visits = visitsOpen()
    ? `
    <div class="disclosure-banner-question" id="disclosure-banner-visits" role="group" aria-labelledby="disclosure-banner-visits-text">
      <p class="disclosure-banner-text" id="disclosure-banner-visits-text">${tHtml('disclosureBanner.visits.text')}</p>
      <div class="disclosure-banner-actions">
        <button
          type="button"
          id="disclosure-banner-visits-allow"
          class="disclosure-banner-btn disclosure-banner-btn-primary"
        >${tHtml('disclosureBanner.visits.allow')}</button>
        <button
          type="button"
          id="disclosure-banner-visits-decline"
          class="disclosure-banner-btn disclosure-banner-btn-primary"
        >${tHtml('disclosureBanner.visits.decline')}</button>
      </div>
    </div>`
    : ''
  banner.innerHTML = `${telemetry}${visits}
    <div class="disclosure-banner-actions disclosure-banner-more">
      <button
        type="button"
        id="disclosure-banner-settings"
        class="disclosure-banner-btn disclosure-banner-btn-secondary"
      >${tHtml('disclosureBanner.settings')}</button>
      <a
        id="disclosure-banner-policy"
        class="disclosure-banner-link"
        href="/privacy"
        target="_blank"
        rel="noopener"
      >${tHtml('disclosureBanner.policy')}</a>
    </div>
  `
  document.body.appendChild(banner)
  return banner
}

/** Build + attach the small-viewport badge (pulsing shield icon).
 *  Click expands to the full banner. */
function buildBadge(): HTMLElement {
  const badge = document.createElement('button')
  badge.id = 'disclosure-badge'
  badge.type = 'button'
  badge.className = 'disclosure-badge disclosure-badge--pulse'
  badge.setAttribute('aria-label', t('disclosureBanner.badge.aria'))
  badge.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 2 L4 5 V12 C4 17 7.5 21 12 22 C16.5 21 20 17 20 12 V5 L12 2 Z" fill="currentColor" />
      <path d="M8.5 12 L11 14.5 L15.5 9.5" stroke="white" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  `
  document.body.appendChild(badge)
  return badge
}

/** Wire the standard banner buttons (both questions' answers and the
 *  settings shortcut). Used both when the banner renders directly on a
 *  large viewport AND when it expands from the small-viewport badge on
 *  click. */
function wireBannerHandlers(): void {
  document
    .getElementById('disclosure-banner-allow')
    ?.addEventListener('click', () => answer('essential'), { once: true })

  document
    .getElementById('disclosure-banner-decline')
    ?.addEventListener('click', () => answer('off'), { once: true })

  document
    .getElementById('disclosure-banner-visits-allow')
    ?.addEventListener('click', () => answerVisits(true), { once: true })

  document
    .getElementById('disclosure-banner-visits-decline')
    ?.addEventListener('click', () => answerVisits(false), { once: true })

  document
    .getElementById('disclosure-banner-settings')
    ?.addEventListener('click', (ev) => {
      // Opening settings is not a yes: whatever is still open is stored
      // as no, and the panel is where the viewer picks something else.
      const trigger = ev.currentTarget as HTMLElement
      if (telemetryOpen()) answer('off')
      if (visitsOpen()) answerVisits(false)
      closeBanner()
      openPrivacyUI(trigger)
    }, { once: true })
}

/** The first refusal still on screen: where focus goes when the button
 *  that had it disappears, never an "allow", so a stray Enter can never
 *  read as consent. */
function firstDecline(): HTMLElement | null {
  return document.getElementById('disclosure-banner-decline')
    ?? document.getElementById('disclosure-banner-visits-decline')
}

/** Tear down the badge and render the full banner in its place.
 *  Triggered by the user tapping the badge.
 *
 *  Focus management: the user just clicked a button (the badge)
 *  that no longer exists in the DOM, so the browser would drop
 *  focus to <body> by default — bad for keyboard / screen-reader
 *  users. Move focus to "No thanks", not "Allow", so a stray Enter
 *  can never read as consent. */
function expandBadgeToBanner(): void {
  clearPulseTimer()
  document.getElementById('disclosure-badge')?.remove()
  buildBanner()
  wireBannerHandlers()
  firstDecline()?.focus()
}

/** Store the viewer's telemetry answer and apply it. The event is
 *  queued only after the tier is stored, so a refusal sends nothing. */
function answer(tier: Extract<TelemetryTier, 'essential' | 'off'>): void {
  setTier(tier)
  applyTierChange(tier)
  clearLegacySeen()
  emit({
    event_type: 'settings_changed',
    key: 'disclosure_seen',
    value_class: tier === 'essential' ? 'allowed' : 'declined',
  })
  answered('disclosure-banner-telemetry')
}

/** AYNI: store the visit memory answer (no also deletes what was kept). */
function answerVisits(yes: boolean): void {
  setVisitMemoryOn(yes)
  answered('disclosure-banner-visits')
}

/** One question is answered: it leaves the banner, focus moves to what
 *  is left, and the banner goes once nothing is left to ask. */
function answered(questionId: string): void {
  document.getElementById(questionId)?.remove()
  if (hasSeenDisclosure()) {
    closeBanner()
    return
  }
  firstDecline()?.focus()
}

/** Remove the banner and the badge. Tolerates either form being live. */
function closeBanner(): void {
  clearPulseTimer()
  document.getElementById('disclosure-banner')?.remove()
  document.getElementById('disclosure-badge')?.remove()
  mounted = false
}

/**
 * Show the banner if the viewer has not already answered it.
 * Idempotent — a second call while the banner is visible is a no-op.
 * Returns true when the banner (or badge) was actually rendered.
 */
export function showDisclosureBannerIfNeeded(): boolean {
  if (mounted) return false
  if (hasSeenDisclosure()) return false
  mounted = true

  if (isSmallViewport()) {
    const badge = buildBadge()
    badge.addEventListener('click', () => expandBadgeToBanner(), { once: true })
    // Make the badge discoverable to screen-reader users. The
    // full banner is itself an aria-live region (it's a text
    // region the visitor was meant to read); the badge is just
    // a button, so it defers to the app-wide #a11y-announcer.
    // Without this, AT users would only learn about the privacy
    // notice if they happened to tab onto the badge.
    announcePolite(t('disclosureBanner.announce'))
    // Settle the pulse after a few seconds even if the user
    // doesn't tap — pulse is for attention on first appearance,
    // not for steady nagging. Capture the badge in the closure so
    // a stale callback can never strip the pulse class from a
    // *different* badge mounted later (after dispose / re-show).
    // We also store the timer id at module scope so dispose paths
    // can cancel it eagerly.
    clearPulseTimer()
    pulseTimer = setTimeout(() => {
      pulseTimer = null
      badge.classList.remove('disclosure-badge--pulse')
    }, PULSE_DURATION_MS)
  } else {
    buildBanner()
    wireBannerHandlers()
  }

  return true
}

/** Tear down. Idempotent. Exposed for tests. */
export function disposeDisclosureBanner(): void {
  clearPulseTimer()
  document.getElementById('disclosure-banner')?.remove()
  document.getElementById('disclosure-badge')?.remove()
  mounted = false
}

/** Test helper to forget the stored answers. */
export function resetDisclosureForTests(): void {
  try {
    localStorage.removeItem(TIER_STORAGE_KEY)
    localStorage.removeItem(LEGACY_SEEN_KEY)
    localStorage.removeItem(VISITS_ON_STORAGE_KEY)
    localStorage.removeItem(VISITS_OFF_STORAGE_KEY)
  } catch {
    // ignore
  }
}
